import { useMemo, useState } from "react"
import {
  Button,
  RadioGroup,
  RadioGroupItem,
  Select,
  type SelectOption,
} from "~/components/design-system/components"
import {
  DialogBodyV2,
  DialogClose,
  DialogContentV2,
  DialogFooterV2,
  DialogHeaderV2,
  DialogRoot,
  DialogTitle,
} from "~/components/reusable/Dialog"
import type { IBuy } from "~/hooks/useSupplyPlan"
import type { IVariantSummary } from "~/hooks/useVariantTableData"
import { DateFormatType, formatDate } from "~/utils/datetime"
import { Label } from "~/components/reusable/Label"
import { formatNumber } from "~/utils/formatting"
import { formatVariantId } from "~/utils/helpers"
import { showErrorToast } from "~/utils/toaster"
import { useNewBuyQuantity, useVariantBuyFill } from "./useVariantBuyFill"

type Choice = "existing" | "new"

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  // Soonest buy date first; the first is preselected.
  buys: IBuy[]
  variants: IVariantSummary[]
  onCreateNewBuy: () => void
  // Passed on to the new buy, so its previewed quantity is the one CreateBuyDialog opens with.
  defaultWarehouseId?: string
}

export function AddToBuyDialog({ open, onOpenChange, ...props }: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  return (
    <DialogRoot open={open} onOpenChange={(next) => !isSubmitting && onOpenChange(next)}>
      <DialogContentV2 className="max-w-md">
        <AddToBuyForm
          {...props}
          close={() => onOpenChange(false)}
          isSubmitting={isSubmitting}
          setIsSubmitting={setIsSubmitting}
        />
      </DialogContentV2>
    </DialogRoot>
  )
}

// Each option is computed for its own buy date, so they can differ from each other and Triggers.
function quantityHint(totalQuantity: number | undefined, basis: string): string {
  if (totalQuantity === undefined) return "Calculating quantity…"
  if (totalQuantity === 0) return `No units suggested ${basis}. Added with 0 units.`
  const units = totalQuantity === 1 ? "unit" : "units"
  return `Adds ${formatNumber(totalQuantity, 0)} ${units}, suggested ${basis}.`
}

function existingBuyHint(
  totalQuantity: number | undefined,
  skipped: number,
  total: number,
): string {
  const basis = "for this buy's date"
  if (skipped === 0) return quantityHint(totalQuantity, basis)
  if (skipped === total) {
    return total === 1
      ? "Already on this buy. Edit it there instead."
      : `All ${formatNumber(total, 0)} variants are already on this buy. Edit it there instead.`
  }
  const added = total - skipped
  const prefix = `${formatNumber(skipped, 0)}/${formatNumber(total, 0)} variants are already on this buy and will be skipped.`
  if (totalQuantity === undefined) return `${prefix} Calculating quantity…`
  const units = totalQuantity === 1 ? "unit" : "units"
  return `${prefix} Adds ${formatNumber(totalQuantity, 0)} ${units} for the other ${formatNumber(added, 0)}.`
}

function SelectionSummary({ variants }: { variants: IVariantSummary[] }) {
  const [only] = variants
  const skuCount = variants.reduce((sum, v) => sum + v.skuIds.length, 0)
  const skus = `${skuCount} ${skuCount === 1 ? "SKU" : "SKUs"}`
  return (
    <div className="flex flex-col gap-0.5 rounded-lg bg-subtle px-3 py-2.5">
      <span className="label-sm truncate">
        {variants.length === 1 && only
          ? only.variantDisplayName
          : `${formatNumber(variants.length, 0)} variants`}
      </span>
      <span className="paragraph-sm text-fg-muted truncate">
        {variants.length === 1 && only ? `#${formatVariantId(only.variantId)} · ${skus}` : skus}
      </span>
    </div>
  )
}

function AddToBuyForm({
  buys,
  variants,
  onCreateNewBuy,
  defaultWarehouseId,
  close,
  isSubmitting,
  setIsSubmitting,
}: Omit<Props, "open" | "onOpenChange"> & {
  close: () => void
  isSubmitting: boolean
  setIsSubmitting: (value: boolean) => void
}) {
  const [choice, setChoice] = useState<Choice>("existing")
  const [buyId, setBuyId] = useState<number | undefined>(buys[0]?.id)

  const options: SelectOption<number>[] = buys.map((buy) => ({
    label: buy.name,
    value: buy.id,
  }))
  const buysById = new Map(buys.map((buy) => [buy.id, buy]))
  const selectedBuy = buyId !== undefined ? buysById.get(buyId) : undefined
  const variantIds = useMemo(() => variants.map((v) => v.variantId), [variants])
  const { totalQuantity, addableVariantIds, isLoadingBuy, addToBuy } = useVariantBuyFill(
    variantIds,
    selectedBuy,
  )
  const newBuyQuantity = useNewBuyQuantity(variantIds, true, defaultWarehouseId)
  const skipped = isLoadingBuy ? 0 : variantIds.length - addableVariantIds.length
  const nothingToAdd = !isLoadingBuy && addableVariantIds.length === 0

  async function handleContinue() {
    if (choice === "new") {
      close()
      onCreateNewBuy()
      return
    }
    if (!selectedBuy) return
    setIsSubmitting(true)
    try {
      await addToBuy()
      close()
    } catch (err) {
      showErrorToast(err)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <DialogHeaderV2>
        <DialogTitle>Add to buy</DialogTitle>
      </DialogHeaderV2>
      <DialogBodyV2 className="py-5 gap-5">
        <SelectionSummary variants={variants} />
        <RadioGroup
          className="gap-5"
          value={choice}
          onValueChange={(value) => setChoice(value as Choice)}
        >
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <RadioGroupItem id="add-to-buy-existing" value="existing" />
              <Label htmlFor="add-to-buy-existing">Add to an existing draft buy</Label>
            </div>
            <div className="pl-6">
              <Select<SelectOption<number>>
                options={options}
                value={options.find((o) => o.value === buyId) ?? null}
                onChange={(option) => setBuyId(option?.value)}
                placeholder="Search buys…"
                isDisabled={choice !== "existing"}
                fullWidthMenu
                formatOptionLabel={(option, { context }) => {
                  const buy = buysById.get(option.value)
                  if (context === "value" || !buy?.buyDate) return option.label
                  return (
                    <div className="flex items-center justify-between gap-4">
                      <span className="truncate">{option.label}</span>
                      <span className="text-fg-subtle shrink-0">
                        {formatDate(new Date(buy.buyDate), DateFormatType.Default)}
                      </span>
                    </div>
                  )
                }}
              />
              {selectedBuy && !isLoadingBuy && (
                <p className="paragraph-sm text-fg-subtle mt-2">
                  {existingBuyHint(totalQuantity, skipped, variantIds.length)}
                </p>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <RadioGroupItem id="add-to-buy-new" value="new" />
              <Label htmlFor="add-to-buy-new">Create a new buy</Label>
            </div>
            <p className="paragraph-sm text-fg-subtle pl-6">
              {quantityHint(newBuyQuantity, "with today as buy date")}
            </p>
          </div>
        </RadioGroup>
      </DialogBodyV2>
      <DialogFooterV2 className="justify-end gap-3">
        <DialogClose asChild>
          <Button variant="neutral" size="sm" disabled={isSubmitting}>
            Cancel
          </Button>
        </DialogClose>
        <Button
          requires="edit"
          size="sm"
          disabled={choice === "existing" && (!selectedBuy || isLoadingBuy || nothingToAdd)}
          loading={isSubmitting}
          onClick={() => void handleContinue()}
        >
          Continue
        </Button>
      </DialogFooterV2>
    </>
  )
}
